import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Anti-escape prevention tracker.
 * - Activates only when walkingMode is true (user opt-in)
 * - Watches GPS in foreground (PWA limitation honored)
 * - Sends observations every ~30s to backend which detects escapes
 * - Surfaces alert returned from the server
 */
export function useSafeZoneTracker() {
  const [walkingMode, setWalkingMode] = useState(false);
  const [tracking, setTracking] = useState(false);
  const [lastPos, setLastPos] = useState(null);
  const [activeAlert, setActiveAlert] = useState(null);
  const watchIdRef = useRef(null);
  const lastSentRef = useRef(0);
  const lastPosRef = useRef(null);

  const sendObservation = useCallback(async (pos, speedKmh) => {
    try {
      const { data } = await api.post("/safezones/observation", {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy_m: pos.coords.accuracy,
        speed_kmh: speedKmh,
        walking_mode: true,
      });
      if (data.alert) {
        setActiveAlert(data.alert);
        try {
          if ("Notification" in window && Notification.permission === "granted" && navigator.serviceWorker) {
            const reg = await navigator.serviceWorker.ready;
            reg.showNotification(data.alert.title, {
              body: data.alert.message,
              tag: "escape-alert",
              icon: "/icons/icon-192.png",
              vibrate: [300, 100, 300, 100, 300],
              requireInteraction: true,
            });
          }
        } catch {/* ignore */}
      }
    } catch (e) {
      if (e.response?.status !== 402) console.error("safezone observation error", e);
    }
  }, []);

  const onPosition = useCallback((pos) => {
    setLastPos(pos);
    const now = Date.now();
    const interval = 30000; // throttle to one update per 30s

    let speedKmh = null;
    if (pos.coords.speed != null && !Number.isNaN(pos.coords.speed)) {
      speedKmh = Math.max(0, pos.coords.speed * 3.6);
    } else if (lastPosRef.current) {
      const prev = lastPosRef.current;
      const dtSec = (pos.timestamp - prev.timestamp) / 1000;
      if (dtSec > 0) {
        const dKm = haversine(
          pos.coords.latitude, pos.coords.longitude,
          prev.coords.latitude, prev.coords.longitude
        );
        speedKmh = (dKm / dtSec) * 3600;
      }
    }
    lastPosRef.current = pos;

    if (now - lastSentRef.current >= interval) {
      lastSentRef.current = now;
      sendObservation(pos, speedKmh);
    }
  }, [sendObservation]);

  const start = useCallback(async () => {
    if (!("geolocation" in navigator)) {
      toast.error("Geolocalizzazione non supportata dal browser");
      return;
    }
    setWalkingMode(true);
    setTracking(true);
    try {
      watchIdRef.current = navigator.geolocation.watchPosition(
        onPosition,
        (err) => {
          console.warn("watchPosition error", err);
          if (err.code === 1) toast.error("Permesso geolocalizzazione negato");
        },
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
      );
    } catch (e) {
      console.error(e);
    }
  }, [onPosition]);

  const stop = useCallback(() => {
    if (watchIdRef.current != null) {
      try { navigator.geolocation.clearWatch(watchIdRef.current); } catch {/* */}
    }
    watchIdRef.current = null;
    setWalkingMode(false);
    setTracking(false);
  }, []);

  useEffect(() => () => {
    if (watchIdRef.current != null) {
      try { navigator.geolocation.clearWatch(watchIdRef.current); } catch {/* */}
    }
  }, []);

  return { walkingMode, tracking, lastPos, activeAlert, setActiveAlert, start, stop };
}

function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon/2)**2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
